import { addSerialNumberToCsv } from "../lib/csv";
import { formatDateValue } from "../lib/dateFormat";
import DateInput from "../components/DateInput";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

const C_ASSIGNED = "#2563eb";
const C_MISSING = "#d97706";

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div
        className="mt-2 text-2xl font-semibold"
        style={{ color: accent || "#0f172a" }}
      >
        {value}
      </div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function PoDayCodeStatusDetail() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [options, setOptions] = useState({ countries: [], consignees: [] });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [country, setCountry] = useState("");
  const [consignee, setConsignee] = useState("");
  const [assignment, setAssignment] = useState("");
  const [search, setSearch] = useState("");

  // Overlapping filter requests can resolve out of order; without these guards a
  // stale answer overwrites a newer one and the page shows the wrong filter.
  const summarySeq = useRef(0);
  const detailSeq = useRef(0);

  useEffect(() => {
    api
      .get("/ceo/filter-options")
      .then((r) =>
        setOptions({
          countries: r.data?.countries || [],
          consignees: r.data?.consignees || [],
        })
      )
      .catch(() => setOptions({ countries: [], consignees: [] }));
  }, []);

  const body = useMemo(
    () => ({
      from_date: fromDate,
      to_date: toDate,
      country,
      consignee,
      assignment,
    }),
    [fromDate, toDate, country, consignee, assignment]
  );

  const load = useCallback(async () => {
    const seq = ++summarySeq.current;
    setLoading(true);
    setError("");
    try {
      const r = await api.post("/ai/ceo-chat/po-day-code-status", body);
      if (seq !== summarySeq.current) return;
      setSummary(r.data);
    } catch (err) {
      if (seq !== summarySeq.current) return;
      setError(formatErr(err));
    } finally {
      if (seq === summarySeq.current) setLoading(false);
    }
  }, [body]);

  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current;
    setDetailLoading(true);
    setDetailError("");
    try {
      const r = await api.post("/ai/ceo-chat/po-day-code-status/detail", {
        ...body,
        limit: 2000,
      });
      if (seq !== detailSeq.current) return;
      setDetail(r.data);
    } catch (err) {
      if (seq !== detailSeq.current) return;
      setDetailError(formatErr(err));
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }, [body]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const totals = summary?.totals || {};
  const dq = summary?.data_quality || {};
  const byCountry = useMemo(
    () => summary?.not_assigned_by_country || [],
    [summary]
  );
  const maxMissing = useMemo(
    () => Math.max(0, ...byCountry.map((c) => c.not_assigned || 0)),
    [byCountry]
  );

  const filteredDetail = useMemo(() => {
    const list = detail?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.po_no, r.consignee, r.country, r.day_codes, r.po_date]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [detail, search]);

  const exportCsv = useCallback(() => {
    const head = [
      "Order No",
      "Order Date",
      "Consignee",
      "Country",
      "Day Code Status",
      "Day Codes",
      "Production Dates",
      "Day Codes Count",
      "Ordered Cartons",
    ];
    const lines = [head.join(",")].concat(
      filteredDetail.map((r) =>
        [
          r.po_no,
          r.po_date,
          r.consignee,
          r.country,
          r.assignment_label,
          r.day_codes,
          r.production_dates,
          r.day_code_count,
          r.ordered_mcs,
        ]
          .map(csvEscape)
          .join(",")
      )
    );
    const blob = new Blob([addSerialNumberToCsv(lines.join("\n"))], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "day-codes-by-order.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredDetail]);

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setCountry("");
    setConsignee("");
    setAssignment("");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900">
              Day Codes — Assigned vs Not Assigned
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              A day code is the production code printed on the carton. It is
              given to an order through the quality code list. Orders with no
              code list yet cannot have their cartons coded, so they are the ones
              to chase.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/ceo-command-center?tab=po")}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Orders
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              From Date
              <DateInput
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              To Date
              <DateInput
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Country
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="mt-1 block max-w-[190px] rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              >
                <option value="">All Countries</option>
                {options.countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Consignee
              <select
                value={consignee}
                onChange={(e) => setConsignee(e.target.value)}
                className="mt-1 block max-w-[220px] rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              >
                <option value="">All Consignees</option>
                {options.consignees.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Day Code
              <select
                value={assignment}
                onChange={(e) => setAssignment(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              >
                <option value="">All Orders</option>
                <option value="assigned">Assigned</option>
                <option value="not_assigned">Not assigned</option>
              </select>
            </label>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Reset Filters
            </button>
          </div>
          <div className="mt-2 text-xs text-slate-500">

            {!fromDate && !toDate
              ? "No date filter applied — the full order history is shown."
              : ""}{" "}
            The headline split always covers every order in scope, so the two
            figures add up even when the Day Code filter is set.
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : null}

        {!loading && summary ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <StatTile
                label="Orders in Scope"
                value={numFmt.format(totals.total_pos || 0)}
                sub="Purchase orders matching the filters"
              />
              <StatTile
                label="Day Code Assigned"
                value={numFmt.format(totals.assigned || 0)}
                sub="A quality code list exists"
                accent={C_ASSIGNED}
              />
              <StatTile
                label="Not Assigned"
                value={numFmt.format(totals.not_assigned || 0)}
                sub="No code list yet — needs action"
                accent={C_MISSING}
              />
              <StatTile
                label="Assigned Share"
                value={
                  totals.assigned_pct != null
                    ? `${pctFmt.format(totals.assigned_pct)}%`
                    : "—"
                }
                sub="Of the orders in scope"
              />
              <StatTile
                label="Cartons Without a Day Code"
                value={numFmt.format(totals.not_assigned_mcs || 0)}
                sub="Ordered cartons on those orders"
                accent={C_MISSING}
              />
            </div>

            {byCountry.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">
                  Where the missing day codes are
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Orders still without a day code, by destination country.
                </div>
                <div className="mt-4 space-y-2">
                  {byCountry.map((c) => (
                    <div key={c.country} className="flex items-center gap-3">
                      <div className="w-48 shrink-0 truncate text-xs text-slate-700">
                        {c.country}
                      </div>
                      <div className="h-3 flex-1 rounded-full bg-slate-100">
                        <div
                          className="h-3 rounded-full"
                          style={{
                            width: `${
                              maxMissing > 0
                                ? Math.max(
                                    (c.not_assigned / maxMissing) * 100,
                                    0.5
                                  )
                                : 0
                            }%`,
                            backgroundColor: C_MISSING,
                          }}
                        />
                      </div>
                      <div className="w-40 shrink-0 text-right text-xs text-slate-700">
                        <span className="font-semibold text-slate-900">
                          {numFmt.format(c.not_assigned)}
                        </span>{" "}
                        of {numFmt.format(c.total_pos)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Orders and their day codes
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading
                      ? "Loading…"
                      : detail?.truncated
                      ? `Showing ${numFmt.format(
                          filteredDetail.length
                        )} of ${numFmt.format(
                          detail.matching_orders || 0
                        )} orders — narrow the filters to see them all`
                      : `${numFmt.format(filteredDetail.length)} order(s)`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search order, consignee, code"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={exportCsv}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              {detailLoading ? (
                <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Loading orders…
                </div>
              ) : null}

              {detailError ? (
                <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                  The order list could not be loaded: {detailError} The figures
                  above are unaffected.
                </div>
              ) : null}

              <div
                className={`mt-3 overflow-x-auto${
                  detailLoading || detailError ? " hidden" : ""
                }`}
              >
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Order No</th>
                      <th className="py-2 pr-3">Order Date</th>
                      <th className="py-2 pr-3">Consignee</th>
                      <th className="py-2 pr-3">Country</th>
                      <th className="py-2 pr-3">Day Code</th>
                      <th className="py-2 pr-3">Codes</th>
                      <th className="py-2 pr-3">Production Dates</th>
                      <th className="py-2 pr-3 text-right">Ordered Cartons</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDetail.slice(0, 300).map((r, i) => (
                      <tr
                        key={`${r.po_id}-${i}`}
                        className="border-b border-slate-100"
                      >
                        <td className="py-2 pr-3 font-medium text-slate-800">
                          {r.po_no}
                        </td>
                        <td className="py-2 pr-3 text-slate-600">
                          {formatDateValue(r.po_date)}
                        </td>
                        <td className="py-2 pr-3 text-slate-700">
                          {r.consignee}
                        </td>
                        <td className="py-2 pr-3 text-slate-600">
                          {r.country}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`rounded-full border px-2 py-0.5 text-xs ${
                              r.assigned
                                ? "border-blue-200 bg-blue-50 text-blue-800"
                                : "border-amber-200 bg-amber-50 text-amber-800"
                            }`}
                          >
                            {r.assignment_label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-slate-700">
                          {r.day_codes || "—"}
                        </td>
                        <td className="py-2 pr-3 text-slate-600">
                          {r.production_dates?.split(", ").map((value) => formatDateValue(value)).join(", ") ||
                            (r.unreadable_day_codes > 0
                              ? "Code not in the standard format"
                              : "—")}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-800">
                          {numFmt.format(r.ordered_mcs || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDetail.length > 300 ? (
                  <div className="mt-2 text-xs text-slate-500">
                    Showing the first 300 rows on screen. Export CSV includes all{" "}
                    {numFmt.format(filteredDetail.length)}.
                  </div>
                ) : null}
              </div>
            </div>

          </>
        ) : null}
      </div>
    </div>
  );
}
